import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import {
  CreditCard,
  Calculator,
  Users,
  AppWindow,
  Check,
  X,
  Loader2,
  RefreshCw,
  AlertCircle,
  ArrowUpRight,
  AlertTriangle,
  CalendarDays,
  Sparkles,
  Crown,
  Zap,
  FileText,
  Banknote,
  Wifi,
  WifiOff,
} from 'lucide-react';

const APP_ICONS = {
  'nexus-compta': Calculator,
  'nexus-sirh': Users,
  compta: Calculator,
  sirh: Users,
};

const APP_NAMES = {
  'nexus-compta': 'Nexus Compta',
  'nexus-sirh': 'Nexus SIRH',
};

const STATUS_BADGE = {
  active: { label: 'Actif', className: 'bg-green-100 text-green-700' },
  trialing: { label: 'Essai', className: 'bg-yellow-100 text-yellow-700' },
  trial: { label: 'Essai', className: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Annule', className: 'bg-red-100 text-red-700' },
  canceled: { label: 'Annule', className: 'bg-red-100 text-red-700' },
  cancel_scheduled: { label: 'Annulation prevue', className: 'bg-orange-100 text-orange-700' },
  past_due: { label: 'Impaye', className: 'bg-red-100 text-red-700' },
  suspended: { label: 'Suspendu', className: 'bg-red-100 text-red-700' },
  inactive: { label: 'Inactif', className: 'bg-slate-100 text-slate-500' },
};

const INVOICE_STATUS = {
  paid: { label: 'Paye', className: 'text-green-700 bg-green-50' },
  pending: { label: 'En attente', className: 'text-yellow-700 bg-yellow-50' },
  overdue: { label: 'En retard', className: 'text-red-700 bg-red-50' },
  draft: { label: 'Brouillon', className: 'text-slate-500 bg-slate-50' },
};

const PLAN_ICONS = {
  starter: Zap,
  pro: Crown,
  enterprise: Sparkles,
};

const PAYMENT_METHOD_LABELS = {
  card: 'Carte bancaire',
  sepa: 'Prelevement SEPA',
  sepa_debit: 'Prelevement SEPA',
  free: 'Gratuit',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr.slice(0, 10);
  }
};

const Subscriptions = () => {
  const { fetchUser } = useAuth();
  const [detailedSubs, setDetailedSubs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscribeModal, setSubscribeModal] = useState(null);
  const [changePlanModal, setChangePlanModal] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detailedRes, pkgRes] = await Promise.allSettled([
        api.get('/api/subscriptions/current-detailed'),
        api.get('/api/subscriptions/packages'),
      ]);
      if (detailedRes.status === 'fulfilled') {
        const data = detailedRes.value.data;
        setDetailedSubs(Array.isArray(data) ? data : []);
      }
      if (pkgRes.status === 'fulfilled') {
        const data = pkgRes.value.data;
        setPackages(Array.isArray(data) ? data : data.items || []);
      }
    } catch (err) {
      setError('Impossible de charger les abonnements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubscribe = async () => {
    if (!subscribeModal || !selectedPlan) return;
    setProcessing(true);
    try {
      const res = await api.post('/api/subscriptions/create-checkout', {
        app_code: subscribeModal.code,
        package_code: selectedPlan.code,
        plan_type: 'monthly',
        payment_method: 'card',
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success('Abonnement cree avec succes');
        setSubscribeModal(null);
        setSelectedPlan(null);
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la souscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleStartTrial = async () => {
    if (!subscribeModal || !selectedPlan) return;
    setProcessing(true);
    try {
      const res = await api.post('/api/subscriptions/start-trial', {
        app_code: subscribeModal.code,
        package_code: selectedPlan.code,
      });
      const newToken = res.data?.access_token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        await fetchUser();
      }
      toast.success('Essai gratuit de 7 jours active');
      setSubscribeModal(null);
      setSelectedPlan(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de demarrer l'essai");
    } finally {
      setProcessing(false);
    }
  };

  const handleChangePlan = async () => {
    if (!changePlanModal || !selectedPlan) return;
    setProcessing(true);
    try {
      await api.put(`/api/subscriptions/${changePlanModal.id}/change-plan`, {
        package_id: selectedPlan.id,
      });
      toast.success('Plan modifie avec succes');
      setChangePlanModal(null);
      setSelectedPlan(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du changement de plan');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setProcessing(true);
    try {
      await api.post(`/api/subscriptions/${cancelModal.id}/cancel`);
      toast.success('Abonnement annule');
      setCancelModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setProcessing(false);
    }
  };

  const subscribedAppCodes = detailedSubs.filter((d) => d.is_subscribed).map((d) => d.app_code);

  const packagesByApp = packages.reduce((acc, pkg) => {
    const code = pkg.app_code || pkg.app?.code || 'other';
    if (!acc[code]) acc[code] = [];
    acc[code].push(pkg);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-100 rounded w-48 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
              <div className="h-12 bg-slate-100 rounded w-12 mb-4" />
              <div className="h-5 bg-slate-100 rounded w-32 mb-2" />
              <div className="h-4 bg-slate-100 rounded w-48" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Erreur</h3>
        <p className="text-slate-500 mb-6">{error}</p>
        <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors">
          <RefreshCw className="w-4 h-4" /> Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Abonnements</h1>
        <p className="text-slate-500 text-sm mt-1">Gerez vos abonnements aux applications Nexus</p>
      </div>

      {/* Detailed Subscriptions */}
      {detailedSubs.length > 0 && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-slate-900">Vos applications</h2>
          <div className="space-y-6">
            {detailedSubs.map((item) => {
              const appCode = item.app_code || '';
              const SubIcon = APP_ICONS[appCode] || AppWindow;
              const nexusSub = item.nexus_subscription;
              const appSub = item.app_subscription;
              const pkg = item.package;
              const invoices = item.invoices || [];
              const isSubscribed = item.is_subscribed;

              // Determine status from app subscription or nexus subscription
              const subStatus = appSub?.status || nexusSub?.status || 'inactive';
              const statusInfo = STATUS_BADGE[subStatus] || STATUS_BADGE.inactive;

              // Payment method
              const paymentMethod = appSub?.payment_method_type || nexusSub?.payment_method_type;
              const paymentLabel = PAYMENT_METHOD_LABELS[paymentMethod] || paymentMethod || '--';

              // Period dates
              const periodStart = appSub?.current_period_start || nexusSub?.current_period_start;
              const periodEnd = appSub?.current_period_end || nexusSub?.current_period_end;

              // Price info from package or subscription
              const priceHt = pkg?.price_monthly_ht ?? appSub?.amount_ht ?? nexusSub?.amount_ht;
              const priceTtc = pkg?.price_monthly_ttc ?? appSub?.amount_ttc ?? nexusSub?.amount_ttc;
              const planType = appSub?.plan_type || nexusSub?.plan_type;

              if (!isSubscribed) {
                return (
                  <div key={appCode} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm opacity-70">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center">
                          <SubIcon className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{item.app_name || APP_NAMES[appCode] || appCode}</h3>
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                            Non abonne
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => { setSubscribeModal({ code: appCode, name: item.app_name || appCode }); setSelectedPlan(null); }}
                        className="px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors"
                      >
                        S'abonner
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={appCode} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Header */}
                  <div className="p-6 pb-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-lg bg-nexus-50 flex items-center justify-center">
                          <SubIcon className="w-5 h-5 text-nexus-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{item.app_name || APP_NAMES[appCode] || appCode}</h3>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                      {appSub || nexusSub ? (
                        <div className="flex items-center gap-1.5 text-xs text-green-600">
                          <Wifi className="w-3.5 h-3.5" />
                          <span>Connecte</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-xs text-slate-400">
                          <WifiOff className="w-3.5 h-3.5" />
                          <span>Donnees non disponibles</span>
                        </div>
                      )}
                    </div>

                    {/* Subscription details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm mb-4">
                      {(pkg?.name || nexusSub?.package_code) && (
                        <div className="flex justify-between sm:justify-start sm:gap-2">
                          <span className="text-slate-500">Pack</span>
                          <span className="font-medium text-slate-900">{pkg?.name || nexusSub?.package_code}</span>
                        </div>
                      )}
                      {priceHt != null && (
                        <div className="flex justify-between sm:justify-start sm:gap-2">
                          <span className="text-slate-500">Prix</span>
                          <span className="font-medium text-slate-900">
                            {Number(priceHt).toFixed(0)} EUR HT / {planType === 'annual' ? 'an' : 'mois'}
                            {priceTtc != null && (
                              <span className="text-slate-400 text-xs ml-1">({Number(priceTtc).toFixed(2)} EUR TTC)</span>
                            )}
                          </span>
                        </div>
                      )}
                      {periodStart && periodEnd && (
                        <div className="flex justify-between sm:justify-start sm:gap-2">
                          <span className="text-slate-500 flex items-center gap-1">
                            <CalendarDays className="w-3.5 h-3.5" /> Periode
                          </span>
                          <span className="font-medium text-slate-900">
                            du {formatDate(periodStart)} au {formatDate(periodEnd)}
                          </span>
                        </div>
                      )}
                      {paymentMethod && (
                        <div className="flex justify-between sm:justify-start sm:gap-2">
                          <span className="text-slate-500 flex items-center gap-1">
                            <Banknote className="w-3.5 h-3.5" /> Paiement
                          </span>
                          <span className="font-medium text-slate-900">{paymentLabel}</span>
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                      <button
                        onClick={() => { setChangePlanModal(nexusSub); setSelectedPlan(null); }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                        Changer de plan
                      </button>
                      <button
                        onClick={() => setCancelModal(nexusSub)}
                        className="px-3 py-2 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>

                  {/* Invoices section */}
                  {invoices.length > 0 && (
                    <div className="border-t border-slate-100 bg-slate-50/50 px-6 py-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                        <FileText className="w-4 h-4" />
                        Dernieres factures
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                              <th className="pb-2 font-medium">Numero</th>
                              <th className="pb-2 font-medium">Date</th>
                              <th className="pb-2 font-medium text-right">Montant TTC</th>
                              <th className="pb-2 font-medium text-right">Statut</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {invoices.slice(0, 5).map((inv, idx) => {
                              const invStatus = INVOICE_STATUS[inv.status] || INVOICE_STATUS.pending;
                              return (
                                <tr key={inv.invoice_number || idx} className="hover:bg-white/60">
                                  <td className="py-2 font-medium text-slate-900">{inv.invoice_number || '--'}</td>
                                  <td className="py-2 text-slate-600">{formatDate(inv.invoice_date)}</td>
                                  <td className="py-2 text-right font-medium text-slate-900">
                                    {inv.amount_ttc != null ? `${Number(inv.amount_ttc).toFixed(2)} EUR` : '--'}
                                  </td>
                                  <td className="py-2 text-right">
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${invStatus.className}`}>
                                      {invStatus.label}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Packages */}
      {Object.keys(packagesByApp).length > 0 && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-slate-900">Offres disponibles</h2>
          {Object.entries(packagesByApp).map(([appCode, pkgs]) => {
            const isSubscribed = subscribedAppCodes.includes(appCode);
            const AppIcon = APP_ICONS[appCode] || AppWindow;
            return (
              <div key={appCode} className="space-y-4">
                <div className="flex items-center gap-2">
                  <AppIcon className="w-5 h-5 text-nexus-600" />
                  <h3 className="font-semibold text-slate-900">{APP_NAMES[appCode] || appCode}</h3>
                  {isSubscribed && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Abonne</span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pkgs.map((pkg) => {
                    const PlanIcon = PLAN_ICONS[pkg.plan_type] || PLAN_ICONS[pkg.code] || Zap;
                    const isPopular = pkg.popular || pkg.code === 'pro';
                    return (
                      <div
                        key={pkg.id}
                        className={cn(
                          'bg-white rounded-xl border p-5 relative transition-all hover:shadow-md',
                          isPopular ? 'border-nexus-300 ring-1 ring-nexus-100' : 'border-slate-200'
                        )}
                      >
                        {isPopular && (
                          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                            <span className="bg-nexus-600 text-white text-xs font-medium px-3 py-0.5 rounded-full">
                              Populaire
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-3">
                          <PlanIcon className="w-5 h-5 text-nexus-600" />
                          <h4 className="font-semibold text-slate-900">{pkg.name || pkg.nom}</h4>
                        </div>
                        <div className="mb-4">
                          <span className="text-3xl font-bold text-slate-900">
                            {pkg.price_monthly_ht != null ? pkg.price_monthly_ht.toFixed(0) : pkg.price_monthly_ttc != null ? pkg.price_monthly_ttc.toFixed(0) : '--'}
                          </span>
                          <span className="text-sm text-slate-500"> EUR HT / mois</span>
                        </div>
                        {(pkg.features || pkg.modules) && Array.isArray(pkg.features || pkg.modules) && (
                          <ul className="space-y-2 mb-5">
                            {(pkg.features || pkg.modules).map((feat, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <span>{feat}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {!isSubscribed && (
                          <button
                            onClick={() => { setSubscribeModal({ code: appCode, name: appCode }); setSelectedPlan(pkg); }}
                            className={cn(
                              'w-full py-2 rounded-lg text-sm font-medium transition-colors',
                              isPopular
                                ? 'bg-nexus-600 text-white hover:bg-nexus-700'
                                : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                            )}
                          >
                            S'abonner
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detailedSubs.length === 0 && Object.keys(packagesByApp).length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
          <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun abonnement</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Vous n'avez pas encore d'abonnement. Decouvrez nos offres dans le catalogue d'applications.
          </p>
        </div>
      )}

      {/* Subscribe Modal */}
      {subscribeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setSubscribeModal(null); setSelectedPlan(null); }} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Confirmer l'abonnement</h3>
              <button onClick={() => { setSubscribeModal(null); setSelectedPlan(null); }} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            {selectedPlan && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-600">Application : <span className="font-medium text-slate-900">{subscribeModal.name}</span></p>
                <p className="text-sm text-slate-600 mt-1">Plan : <span className="font-medium text-slate-900">{selectedPlan.name || selectedPlan.nom}</span></p>
                <p className="text-sm text-slate-600 mt-1">
                  Prix : <span className="font-bold text-slate-900">{selectedPlan.price_monthly_ttc != null ? selectedPlan.price_monthly_ttc.toFixed(2) : '--'} EUR TTC</span>
                  <span className="text-slate-500"> / mois</span>
                </p>
              </div>
            )}
            <div className="space-y-2">
              <button onClick={handleStartTrial} disabled={processing} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                {processing && <Loader2 className="w-4 h-4 animate-spin" />}
                Demarrer l'essai gratuit (7 jours)
              </button>
              <button onClick={handleSubscribe} disabled={processing} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 disabled:opacity-50 transition-colors">
                {processing && <Loader2 className="w-4 h-4 animate-spin" />}
                Souscrire maintenant
              </button>
              <button onClick={() => { setSubscribeModal(null); setSelectedPlan(null); }} className="w-full px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                Annuler
              </button>
            </div>
            <p className="text-xs text-slate-500 text-center mt-3">
              L'essai gratuit ne necessite pas de carte bancaire. Vous serez notifie 2 jours avant la fin.
            </p>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {changePlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setChangePlanModal(null); setSelectedPlan(null); }} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Changer de plan</h3>
              <button onClick={() => { setChangePlanModal(null); setSelectedPlan(null); }} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Plan actuel : <span className="font-medium text-slate-700">{changePlanModal.package?.name || changePlanModal.plan_name || changePlanModal.package_code || 'Standard'}</span>
            </p>
            <div className="space-y-3 mb-6">
              {(packagesByApp[changePlanModal.app_code || changePlanModal.app?.code] || []).map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => setSelectedPlan(pkg)}
                  className={cn(
                    'w-full flex items-center justify-between p-4 rounded-lg border text-left transition-colors',
                    selectedPlan?.id === pkg.id
                      ? 'border-nexus-500 bg-nexus-50 ring-1 ring-nexus-200'
                      : 'border-slate-200 hover:bg-slate-50'
                  )}
                >
                  <div>
                    <p className="font-medium text-slate-900">{pkg.name || pkg.nom}</p>
                    <p className="text-sm text-slate-500">{pkg.price_monthly_ttc != null ? pkg.price_monthly_ttc.toFixed(2) : '--'} EUR TTC / mois</p>
                  </div>
                  {selectedPlan?.id === pkg.id && (
                    <div className="w-5 h-5 rounded-full bg-nexus-600 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => { setChangePlanModal(null); setSelectedPlan(null); }} className="flex-1 px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                Annuler
              </button>
              <button onClick={handleChangePlan} disabled={processing || !selectedPlan} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 disabled:opacity-50 transition-colors">
                {processing && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirmer le changement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {cancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCancelModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Annuler l'abonnement ?</h3>
              <p className="text-sm text-slate-500 mb-6">
                L'abonnement a {cancelModal.app?.name || cancelModal.app_name || 'cette application'} sera annule.
                L'acces sera maintenu jusqu'a la fin de la periode en cours.
              </p>
              <div className="flex items-center gap-3 w-full">
                <button onClick={() => setCancelModal(null)} className="flex-1 px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                  Garder
                </button>
                <button onClick={handleCancel} disabled={processing} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors">
                  {processing && <Loader2 className="w-4 h-4 animate-spin" />}
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
